import { Route, Routes } from '@angular/router';
import { languages } from 'utils/languages';
import { routes as translatedRoutes } from '../../utils/routes';
import { deactivateGuard } from './_shared/guards/can-component-deactivate';
import { userGuard } from './common/user/user.guard';
import { removeDuplicatesByKey } from './common/utils/helpers';
import { languageGuard } from './language.guard';
import { canActivateMaintenanceFn } from './pages/maintenance/maintenance';
import { messagesGuard } from './pages/messages/messages.guard';
import { IndexType } from './pages/search-results/index/index.component';
import { searchEngineResolver } from './pages/search-results/search-engine.resolver';

const translateCategory = (lang: string) => {
  switch (lang) {
    case 'it':
      return '/natura';
    case 'en':
      return '/nature';
    case 'es':
      return '/naturaleza';
    case 'fr':
      return '/nature';
    case 'de':
      return '/natur';
    default:
      return '/nature';
  }
};

const langChildren = removeDuplicatesByKey(
  [
    { path: '', canActivate: [languageGuard], children: [] },
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.explore'],
      loadComponent: () => import('./pages/explore/explore.component').then(c => c.ExploreComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.wonders-map'],
      loadComponent: () => import('./pages/homepage/homepage.component').then(c => c.HomepageComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.search-results'],
      loadChildren: () => import('./pages/search-results/search-results.routes').then(m => m.SEARCH_ROUTES),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.category'],
      loadComponent: () => import('./pages/search-results/index/index.component').then(m => m.IndexComponent),
      data: {
        indexType: IndexType.Category,
      },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.category/:category'],
      loadComponent: () => import('./pages/search-results/search-engine.component').then(m => m.SearchEngineComponent),
      resolve: {
        additional: searchEngineResolver,
      },
      data: {
        mode: 'list',
        title: 'meta.search-results.category-{{category}}.title',
        description: 'meta.search-results.category-{{category}}.destranslatedRoutes[l]iption',
        isProvince: false,
        isRegion: false,
        isMunicipality: false,
        prefix: 'title',
        refresh: true,
      },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.find/what-to-do-in'],
      loadComponent: () => import('./pages/search-results/index/index.component').then(m => m.IndexComponent),
      data: {
        indexType: IndexType.RegionAll,
      },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.find/what-to-do-in/:location'],
      loadComponent: () => import('./pages/search-results/search-engine.component').then(m => m.SearchEngineComponent),
      resolve: { additional: searchEngineResolver },
      data: {
        title: 'meta.search-results.region-all.title',
        description: 'meta.search-results.region-all.description',
        isProvince: false,
        isRegion: true,
        isMunicipality: false,
        prefix: 'location',
        refresh: true,
      },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.find/what-to-do-at'],
      loadComponent: () => import('./pages/search-results/index/index.component').then(m => m.IndexComponent),
      data: {
        indexType: IndexType.ProvinceAll,
      },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.find/what-to-do-at/:location'],
      loadComponent: () => import('./pages/search-results/search-engine.component').then(m => m.SearchEngineComponent),
      resolve: { additional: searchEngineResolver },
      data: {
        title: 'meta.search-results.province-all.title',
        description: 'meta.search-results.province-all.description',
        isProvince: true,
        isRegion: false,
        isMunicipality: false,
        prefix: 'location',
        refresh: true,
      },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.find/what-to-do-near'],
      loadComponent: () => import('./pages/search-results/index/index.component').then(m => m.IndexComponent),
      data: {
        indexType: IndexType.Municipality,
      },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.find/what-to-do-near/:location'],
      loadComponent: () => import('./pages/search-results/search-engine.component').then(m => m.SearchEngineComponent),
      resolve: { additional: searchEngineResolver },
      data: {
        title: 'meta.search-results.municipality-all.title',
        description: 'meta.search-results.municipality-all.description',
        isProvince: false,
        isRegion: false,
        isMunicipality: true,
        prefix: 'location',
        refresh: true,
      },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.find/what-to-visit-in'],
      loadComponent: () => import('./pages/search-results/index/index.component').then(m => m.IndexComponent),
      data: {
        indexType: IndexType.RegionWonder,
      },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.find/what-to-visit-in/:location'],
      loadComponent: () => import('./pages/search-results/search-engine.component').then(m => m.SearchEngineComponent),
      resolve: { additional: searchEngineResolver },
      data: {
        types: ['wonder'],
        title: 'meta.search-results.region-wonder.title',
        description: 'meta.search-results.region-wonder.description',
        isProvince: false,
        isRegion: true,
        isMunicipality: false,
        prefix: 'location',
        refresh: true,
      },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.find/what-to-visit-at'],
      loadComponent: () => import('./pages/search-results/index/index.component').then(m => m.IndexComponent),
      data: {
        indexType: IndexType.ProvinceWonder,
      },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.find/what-to-visit-at/:location'],
      loadComponent: () => import('./pages/search-results/search-engine.component').then(m => m.SearchEngineComponent),
      resolve: { additional: searchEngineResolver },
      data: {
        types: ['wonder'],
        title: 'meta.search-results.province-wonder.title',
        description: 'meta.search-results.province-wonder.description',
        isProvince: true,
        isRegion: false,
        isMunicipality: false,
        prefix: 'location',
        refresh: true,
      },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.find/events-in'],
      loadComponent: () => import('./pages/search-results/index/index.component').then(m => m.IndexComponent),
      data: {
        indexType: IndexType.RegionEventSharry,
      },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.find/events-in/:location'],
      loadComponent: () => import('./pages/search-results/search-engine.component').then(m => m.SearchEngineComponent),
      resolve: { additional: searchEngineResolver },
      data: {
        types: ['event', 'tour', 'experience'],
        title: 'meta.search-results.region-event.title',
        description: 'meta.search-results.region-event.description',
        isProvince: false,
        isRegion: true,
        isMunicipality: false,
        prefix: 'location',
        refresh: true,
      },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.find/events-at'],
      loadComponent: () => import('./pages/search-results/index/index.component').then(m => m.IndexComponent),
      data: {
        indexType: IndexType.ProvinceEventSharry,
      },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.find/events-at/:location'],
      loadComponent: () => import('./pages/search-results/search-engine.component').then(m => m.SearchEngineComponent),
      resolve: { additional: searchEngineResolver },
      data: {
        types: ['event', 'sharry'],
        title: 'meta.search-results.province-event.title',
        description: 'meta.search-results.province-event.description',
        isProvince: true,
        isRegion: false,
        isMunicipality: false,
        prefix: 'location',
        refresh: true,
      },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.discover'],
      redirectTo: translatedRoutes[l]['routes.discover'] + '/sharrytour',
      pathMatch: 'full',
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.discover/:keyword'],
      loadComponent: () => import('./pages/search-results/search-engine.component').then(m => m.SearchEngineComponent),
      resolve: { additional: searchEngineResolver },
      data: {
        isBanner: true,
        title: 'meta.search-results.keyword-{{keyword}}.title',
        description: 'meta.search-results.keyword-{{keyword}}.description',
      },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.trip'],
      loadComponent: () => import('./pages/trip/trip.component').then(m => m.TripComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.faq'],
      loadChildren: () => import('./pages/faq/faq.routes').then(m => m.FAQ_ROUTES),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.contacts'],
      loadComponent: () => import('./pages/contacts/contacts.component').then(m => m.ContactsComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.wonders'],
      loadChildren: () => import('./pages/content/content.routes').then(m => m.WONDER_ROUTES),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.sharry'],
      loadChildren: () => import('./pages/sharry/sharry.routes').then(m => m.SHARRY_ROUTES),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.events'],
      loadChildren: () => import('./pages/event/event.routes').then(m => m.EVENT_ROUTES),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.recommendation-management'],
      canActivate: [userGuard],
      loadComponent: () =>
        import('./pages/recommendation-management/recommendation-management.component').then(
          m => m.RecommendationManagementComponent,
        ),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.recommendation-management/edit/:id'],
      canActivate: [userGuard],
      loadComponent: () => import('./pages/create/create.component').then(m => m.CreateComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.sharry-activity'],
      canActivate: [userGuard],
      loadChildren: () => import('./pages/sharry-activity/sharry-activity.routes').then(m => m.SHARRY_ACTIVITY_ROUTES),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.likes'],
      loadChildren: () => import('./pages/likes/likes.routes').then(m => m.LIKES_ROUTES),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.create'],
      canActivate: [userGuard],
      loadComponent: () => import('./pages/create/create.component').then(m => m.CreateComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.sharry-create'],
      canActivate: [userGuard],
      loadComponent: () => import('./pages/create/shop-create/shop-create.component').then(m => m.ShopCreateComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.not-implemented'],
      loadComponent: () =>
        import('./pages/not-implemented/not-implemented.component').then(m => m.NotImplementedComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.notifications'],
      canActivate: [userGuard],
      loadComponent: () => import('./pages/notifications/notifications.component').then(m => m.NotificationsComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.messages'],
      canActivate: [userGuard, messagesGuard],
      loadComponent: () => import('./pages/messages/messages.component').then(m => m.MessagesComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.profile/home'],
      canActivate: [userGuard],
      loadComponent: () => import('./pages/profile-home/profile-home.component').then(m => m.ProfileHomeComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.profile'],
      loadChildren: () => import('./pages/profile/profile.routes').then(m => m.PROFILE_ROUTES),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.become-pro'],
      loadComponent: () => import('./pages/become-pro/become-pro.component').then(m => m.BecomeProComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.business-trips'],
      loadComponent: () =>
        import('./pages/business-trips/business-trips.component').then(m => m.BusinessTripsComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.become-coordinator'],
      loadComponent: () =>
        import('./pages/become-coordinator/become-coordinator.component').then(m => m.BecomeCoordinatorComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.cherry-meter'],
      loadComponent: () => import('./pages/cherry-meter/cherry-meter.component').then(m => m.CherryMeterComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.dashboard'],
      canActivate: [userGuard],
      loadChildren: () => import('./pages/dashboard/dashboard.routes').then(m => m.DASHBOARD_ROUTES),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.dashboard/sharry-management/edit/:id'],
      canActivate: [userGuard],
      loadComponent: () => import('./pages/create/shop-create/shop-create.component').then(m => m.ShopCreateComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.event-dashboard'],
      canActivate: [userGuard],
      loadChildren: () => import('./pages/event-dashboard/event-dashboard.routes').then(m => m.EVENT_DASHBOARD_ROUTES),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.event-dashboard/events-report/edit/:id'],
      canActivate: [userGuard],
      loadComponent: () => import('./pages/create/create.component').then(m => m.CreateComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.go/:slug'],
      loadComponent: () => import('./pages/go/go.component').then(m => m.GoComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.recommendation-create-introduction'],
      loadComponent: () =>
        import('./pages/recommendation-create-introduction/recommendation-create-introduction.component').then(
          m => m.RecommendationCreateIntroductionComponent,
        ),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.privacy'],
      loadComponent: () =>
        import('./pages/terms-and-privacy/terms-and-privacy.component').then(m => m.TermsAndPrivacyComponent),
      data: { mode: 'privacy' },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.selling-conditions'],
      loadComponent: () =>
        import('./pages/terms-and-privacy/terms-and-privacy.component').then(m => m.TermsAndPrivacyComponent),
      data: { mode: 'selling-conditions' },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.terms'],
      loadComponent: () =>
        import('./pages/terms-and-privacy/terms-and-privacy.component').then(m => m.TermsAndPrivacyComponent),
      data: { mode: 'terms' },
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.ambassador'],
      loadComponent: () => import('./pages/ambassador/ambassador.component').then(m => m.AmbassadorComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.community'],
      loadChildren: () => import('./pages/community/community.routes').then(c => c.COMMUNITY_ROUTES),
    })),
    /*...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.who-we-are'],
      loadComponent: () => import('./pages/who/who.component').then(c => c.WhoComponent),
    })),*/
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.what-is-sharryland'],
      loadComponent: () => import('./pages/what/what.component').then(c => c.WhatComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.gift-card'],
      loadComponent: () => import('./pages/gift-card/gift-card.component').then(m => m.GiftCardComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.added-to-cart'],
      loadComponent: () => import('./pages/added-to-cart/added-to-cart.component').then(m => m.AddedToCartComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.cart'],
      loadComponent: () => import('./pages/cart/cart.component').then(m => m.CartComponent),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.checkout'],
      loadChildren: () => import('./pages/checkout/checkout.routes').then(m => m.CHECKOUT_ROUTES),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.land'],
      loadChildren: () => import('./pages/land/land.routes').then(m => m.LAND_ROUTES),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.likes/shared/:slug'],
      loadComponent: () =>
        import('./pages/like-shared-collection/like-shared-collection.component').then(
          c => c.LikeSharedCollectionComponent,
        ),
    })),
    ...languages.map<Route>(l => ({
      path: translatedRoutes[l]['routes.post-widget/:slug'],
      loadComponent: () =>
        import('./_shared/widget/post-widget/post-widget.component').then(c => c.PostWidgetComponent),
    })),
    // {
    //   path: '**',
    //   redirectTo: '../not-found',
    //   pathMatch: 'full',
    // },
  ],
  'path',
);

export const routes: Routes = [
  {
    path: 'maintenance',
    canActivate: [canActivateMaintenanceFn],
    canDeactivate: [deactivateGuard],
    loadComponent: () => import('./pages/maintenance/maintenance.component').then(m => m.MaintenanceComponent),
  },
  {
    path: 'deprecated',
    canActivate: [canActivateMaintenanceFn],
    canDeactivate: [deactivateGuard],
    loadComponent: () => import('./pages/deprecated/deprecated.component').then(m => m.DeprecatedComponent),
  },
  {
    path: 'go',
    loadComponent: () => import('./pages/go/go.component').then(m => m.GoComponent),
  },
  {
    path: 'go/:slug',
    loadComponent: () => import('./pages/go/go.component').then(m => m.GoComponent),
  },
  {
    path: 'join-chat/:id',
    loadComponent: () => import('./pages/join-chat/join-chat.component').then(m => m.JoinChatComponent),
  },
  {
    path: 'signin',
    loadComponent: () =>
      import('./pages/external-sign-in/external-sign-in.component').then(c => c.ExternalSignInComponent),
  },
  {
    path: 'not-found',
    loadComponent: () => import('./pages/not-found/not-found.component').then(m => m.NotFoundComponent),
  },
  {
    path: 'unauthorized',
    loadComponent: () => import('./pages/unauthorized/unauthorized.component').then(m => m.UnauthorizedComponent),
  },
  {
    path: 'content-not-found',
    loadComponent: () =>
      import('./pages/content-not-found/content-not-found.component').then(m => m.ContentNotFoundComponent),
  },
  {
    path: ':lang',
    canActivate: [languageGuard],
    children: langChildren,
  },
  {
    path: '',
    canActivate: [languageGuard],
    children: [],
  },
  {
    path: '**',
    redirectTo: '/not-found',
    pathMatch: 'full',
  },
];
